import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  fetchAlgoliaProduct,
  searchAlgoliaProducts,
} from '@jane/search/data-access';
import type {
  AlgoliaProduct,
  FetchAlgoliaProductParams,
  SearchAlgoliaProductsParams,
} from '@jane/search/types';
import { getJwt } from '@jane/shared/auth';
import { createUseQueryHook, janeApi } from '@jane/shared/data-access';
import type { PurchasedProduct } from '@jane/shared/types';

import { queryKeys, urls } from './products.util';

const getStoreProduct = async (params: FetchAlgoliaProductParams) => {
  const product = await fetchAlgoliaProduct(params);

  return product;
};

export const useStoreProduct = createUseQueryHook<
  FetchAlgoliaProductParams,
  AlgoliaProduct | null
>(
  getStoreProduct,
  queryKeys.storeProduct,
  (params) => !!params.productId && !!params.storeId
);

const getProducts = async (params: SearchAlgoliaProductsParams) => {
  const products = await searchAlgoliaProducts(params);
  return products;
};

export const useProducts = (
  { enabled, ...params }: SearchAlgoliaProductsParams,
  key = ['products']
) => {
  return useQuery({
    enabled,
    queryFn: () => getProducts({ ...params }),
    queryKey: [...key, params],
  });
};

export const useInfiniteProducts = (
  { enabled, ...params }: SearchAlgoliaProductsParams,
  key = ['infiniteProducts']
) => {
  return useInfiniteQuery({
    enabled,
    queryFn: async ({ pageParam }: { pageParam?: number }) => {
      const data = await getProducts({ ...params, page: pageParam });
      return {
        ...data,
        page: pageParam,
      };
    },
    queryKey: [...key, params],
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const maybeNextPage = (lastPage?.page ?? 0) + 1;
      const hasNextPage = maybeNextPage < (lastPage?.nbPages ?? 0);
      return hasNextPage ? maybeNextPage : undefined;
    },
  });
};

//mutation for fetching a product on demand (i.e. when a user clicks an add button)
export const useFetchStoreProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: FetchAlgoliaProductParams) =>
      queryClient.ensureQueryData({
        queryFn: () => fetchAlgoliaProduct(params),
        queryKey: queryKeys.storeProduct(params),
      }),
  });
};

export interface UsePurchasedProducts {
  limit?: number;
  showUnavailable?: boolean;
  storeId?: number;
  userId?: number;
}

const enableWithStoreId = ({
  storeId,
}: Pick<UsePurchasedProducts, 'storeId'>) => !!storeId;

const fetchPurchasedProducts = async ({
  limit,
  showUnavailable,
  storeId,
}: Omit<UsePurchasedProducts, 'userId'>) => {
  return await janeApi
    .get<{
      purchased: PurchasedProduct[];
    }>(urls.purchasedProducts({ limit, showUnavailable, storeId }), getJwt())
    .then((res) => res.purchased);
};

export const usePurchasedProducts = createUseQueryHook<
  UsePurchasedProducts,
  PurchasedProduct[]
>(fetchPurchasedProducts, queryKeys.purchasedProducts, enableWithStoreId);
